import {
  PRINT_PAGE_SIZE_LABELS,
  type PrintPage,
  type PrintPageSize,
  printPageSizeSchema,
} from '#shared/types';
import { Segmented } from '#client/components/ui/Segmented';
import { Select } from '#client/components/ui/Select';
import { NumberField } from '../controls/NumberField';

interface PageSectionProps {
  page: PrintPage;
  onChange: (patch: Partial<PrintPage>) => void;
}

/**
 * Paper and margins.
 *
 * The margins here are the clear space at the paper's edges, *inside* the
 * header and footer bands rather than in addition to them — which is why they
 * are small numbers next to the band heights on the header and footer sections.
 * A printer that cannot lay ink in the outer 5 mm is the constraint these exist
 * for.
 */
export function PageSection({ page, onChange }: PageSectionProps) {
  return (
    <>
      <Select
        label="Page size"
        value={page.size}
        onChange={(choice) => onChange({ size: choice })}
        options={printPageSizeSchema.options.map((size: PrintPageSize) => ({
          value: size,
          label: PRINT_PAGE_SIZE_LABELS[size],
        }))}
      />

      <Segmented
        label="Orientation"
        value={page.orientation}
        onChange={(orientation) => onChange({ orientation })}
        options={[
          { value: 'portrait', label: 'Portrait' },
          { value: 'landscape', label: 'Landscape' },
        ]}
      />

      <div className="c-builder__grid">
        <NumberField
          label="Top margin"
          unit="mm"
          value={page.margin_top}
          onChange={(margin_top) => onChange({ margin_top })}
          min={0}
          max={60}
          step={0.5}
        />
        <NumberField
          label="Bottom margin"
          unit="mm"
          value={page.margin_bottom}
          onChange={(margin_bottom) => onChange({ margin_bottom })}
          min={0}
          max={60}
          step={0.5}
        />
        <NumberField
          label="Left margin"
          unit="mm"
          value={page.margin_left}
          onChange={(margin_left) => onChange({ margin_left })}
          min={0}
          max={60}
          step={0.5}
        />
        <NumberField
          label="Right margin"
          unit="mm"
          value={page.margin_right}
          onChange={(margin_right) => onChange({ margin_right })}
          min={0}
          max={60}
          step={0.5}
        />
      </div>
    </>
  );
}
